import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axios';
import Layout from '../../common/components/Navbar';
import { notifications } from '@mantine/notifications';
import {
  Box,
  Button,
  Text,
  Card,
  Table,
  Group,
  TextInput,
  Select,
  Badge,
  Tooltip,
  Modal,
  rem,
  ScrollArea,
  Image,
  ActionIcon,
  Divider,
  Stack,
  Paper
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import {
  IconPlus, IconCurrencyPeso, IconUpload, IconEye, 
  IconMoodEmpty, IconFilter, IconSearch
} from '@tabler/icons-react';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  
  // Filters
  const [dateRange, setDateRange] = useState([null, null]);
  const [filterParticulars, setFilterParticulars] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  // View Details Modal State
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  // Form Data
  const [formData, setFormData] = useState({
    title: '',
    particulars: '',
    category: 'maintenance',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  // Separate state for the actual file object
  const [uploadFile, setUploadFile] = useState(null);

  const fetchExpenses = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const config = { 
          headers: { Authorization: `Bearer ${user.token}` },
          params: {
              startDate: dateRange[0],
              endDate: dateRange[1]
          }
      };
      const { data } = await axios.get('/api/expenses', config);
      setExpenses(data);
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Failed to fetch expenses.', color: 'red' });
    }
  }, [dateRange]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleChange = (e) => {
    const { name, value } = e.target; 
    setFormData({ ...formData, [name]: value });
  };
  
  const handleSelectChange = (name, value) => {
      setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setUploadFile(file); // Store raw file for FormData
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.amount || !formData.category || !formData.date) {
        notifications.show({ title: 'Validation Error', message: 'Please fill in all required fields.', color: 'yellow' });
        return;
    }
      
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      
      // Use FormData for File Uploads
      const dataToSend = new FormData();
      dataToSend.append('title', formData.title);
      dataToSend.append('particulars', formData.particulars);
      dataToSend.append('category', formData.category);
      dataToSend.append('amount', formData.amount);
      dataToSend.append('description', formData.description);
      dataToSend.append('date', formData.date);
      
      if (uploadFile) {
        dataToSend.append('proofImage', uploadFile);
      }

      const config = { 
        headers: { 
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'multipart/form-data' // Important for files
        } 
      };

      await axios.post('/api/expenses', dataToSend, config);
      
      setOpen(false);
      // Reset Form
      setFormData({ 
        title: '', 
        particulars: '', 
        category: 'maintenance', 
        amount: '', 
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      setUploadFile(null);

      fetchExpenses();
      notifications.show({ title: 'Success', message: 'Expense recorded successfully.', color: 'green' });
    } catch (error) {
      console.error(error);
      notifications.show({ title: 'Error', message: 'Failed to add expense.', color: 'red' });
    }
  };

  const handleViewDetails = (expense) => {
    setSelectedExpense(expense);
    setViewOpen(true);
  };

  // --- FILTER LOGIC ---
  const filteredExpenses = expenses.filter(expense => {
    // Particulars Filter (Case insensitive check on title OR particulars)
    const searchString = `${expense.title} ${expense.particulars || ''}`.toLowerCase();
    const matchesParticulars = searchString.includes(filterParticulars.toLowerCase());

    // Category Filter
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;

    return matchesParticulars && matchesCategory;
  });

  const totalExpenses = filteredExpenses.reduce((acc, curr) => acc + Number(curr.amount), 0);

  // Mantine Table Rows
  const rows = filteredExpenses.map((expense) => (
    <Table.Tr key={expense._id}>
      <Table.Td>{new Date(expense.date).toLocaleDateString()}</Table.Td>
      <Table.Td>{expense.title}</Table.Td>
      <Table.Td>{expense.particulars || '-'}</Table.Td>
      <Table.Td>
        <Badge color="gray" variant="light" tt="capitalize">
          {expense.category.replace('_', ' ')}
        </Badge>
      </Table.Td>
      <Table.Td ta="right">
        <Text fw={700} c="red">
          -₱{Number(expense.amount).toLocaleString()}
        </Text>
      </Table.Td>
      <Table.Td ta="center">
        <Group justify="center" gap={8}>
            <Tooltip label="View Details">
                <ActionIcon variant="subtle" color="blue" onClick={() => handleViewDetails(expense)}>
                    <IconEye style={{ width: rem(18), height: rem(18) }} />
                </ActionIcon>
            </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Layout>
      <Group justify="space-between" mb="lg">
        <Text component="h1" fz="xl" fw={700}>Expense Tracking</Text>
        <Button 
          variant="filled" 
          color="green"
          leftSection={<IconPlus style={{ width: rem(18) }} />} 
          onClick={() => setOpen(true)}
        >
          Log Expense
        </Button>
      </Group>

      <Card shadow="sm" padding="lg" radius="md" withBorder mb="lg" bg="red.0">
        <Group align="center" gap="md">
          {/* Replaced with Peso Icon */}
          <IconCurrencyPeso style={{ width: rem(40), height: rem(40) }} color="red" />
          <Box>
            <Text fz="xs" c="dimmed">Total Expenditures</Text>
            <Text fz="h2" fw={700} c="red.8">
              ₱{totalExpenses.toLocaleString()}
            </Text>
          </Box>
        </Group>
      </Card>

      {/* FILTERS */}
      <Card shadow="sm" p="md" mb="lg" withBorder>
        {/* CHANGED ALIGNMENT FROM flex-end TO center */}
        <Group align="center"> 
            <Group gap="xs">
                <IconFilter size={20} color="gray" />
                <Text size="sm" fw={500}>Filters:</Text>
            </Group>
            
            <TextInput 
                placeholder="Search Particulars/Title" 
                leftSection={<IconSearch size={16} />}
                value={filterParticulars}
                onChange={(e) => setFilterParticulars(e.target.value)}
                style={{ width: 250 }}
            />

            <Select 
                placeholder="Category" 
                value={filterCategory}
                onChange={setFilterCategory}
                data={[
                    { value: 'all', label: 'All Categories' },
                    { value: 'maintenance', label: 'Maintenance & Repairs' },
                    { value: 'utilities', label: 'Utilities' },
                    { value: 'salaries', label: 'Salaries' },
                    { value: 'supplies', label: 'Supplies' },
                    { value: 'other', label: 'Other' },
                ]}
                allowDeselect={false}
                style={{ width: 200 }}
            />

            <DatePickerInput
              type="range"
              placeholder="Select Date Range"
              value={dateRange}
              onChange={setDateRange}
              maxDate={new Date()}
              style={{ width: 200 }}
              clearable
              color="green" 
              getDayProps={(date) => {
                try {
                    // Force conversion to native Date to handle potential Day.js objects safely
                    const d = new Date(date);
                    const start = dateRange[0] ? new Date(dateRange[0]) : null;
                    const end = dateRange[1] ? new Date(dateRange[1]) : null;

                    // Use standard string comparison to avoid type errors
                    const isStart = start && d.toDateString() === start.toDateString();
                    const isEnd = end && d.toDateString() === end.toDateString();
                    
                    if (isStart || isEnd) {
                        return {
                            style: { 
                                backgroundColor: 'var(--mantine-color-green-filled)', 
                                color: 'var(--mantine-color-white)',
                                fontWeight: 'bold',
                                borderRadius: '4px'
                            },
                            selected: true
                        };
                    }
                } catch (e) {
                    // Fallback if date parsing fails
                    return {};
                }
                return {};
              }}
            />
        </Group>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <ScrollArea h={500}>
            <Table striped highlightOnHover>
                <Table.Thead bg="gray.1">
                    <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Title</Table.Th>
                        <Table.Th>Particulars</Table.Th>
                        <Table.Th>Category</Table.Th>
                        <Table.Th ta="right">Amount</Table.Th>
                        <Table.Th ta="center">Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length > 0 ? rows : (
                        <Table.Tr>
                            <Table.Td colSpan={6} ta="center">
                                <Group gap="xs" justify="center">
                                    <IconMoodEmpty style={{ width: rem(20) }} />
                                    <Text c="dimmed">No expenses found.</Text>
                                </Group>
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </ScrollArea>
      </Card>

      {/* Add Modal */}
      <Modal opened={open} onClose={() => setOpen(false)} title={<Text fw={600}>Log New Expense</Text>} centered>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} style={{ display: 'flex', flexDirection: 'column', gap: rem(15) }}>
            <TextInput required label="Expense Title" name="title" value={formData.title} onChange={handleChange} />
            <TextInput label="Particulars" name="particulars" value={formData.particulars} onChange={handleChange} />
            <Select 
                required label="Category" name="category"
                value={formData.category} onChange={(value) => handleSelectChange('category', value)}
                data={[
                    { value: 'maintenance', label: 'Maintenance & Repairs' },
                    { value: 'utilities', label: 'Utilities' },
                    { value: 'salaries', label: 'Salaries' },
                    { value: 'supplies', label: 'Supplies' },
                    { value: 'other', label: 'Other' },
                ]}
            />
            <TextInput required label="Amount (₱)" name="amount" type="number" value={formData.amount} onChange={handleChange} min={0} />
            <TextInput required label="Date" name="date" type="date" value={formData.date} onChange={handleChange} />
            <Group align="center">
                <Button component="label" variant="outline" leftSection={<IconUpload style={{ width: rem(18) }} />}>
                  Upload Proof <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                </Button>
                {uploadFile && <Text size="sm" c="green">{uploadFile.name}</Text>}
            </Group>
            <TextInput label="Remarks" name="description" value={formData.description} onChange={handleChange} />
            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => setOpen(false)}>Cancel</Button>
                <Button variant="filled" color="green" type="submit">Save</Button>
            </Group>
        </Box>
      </Modal>

      {/* VIEW DETAILS MODAL */}
      <Modal 
        opened={viewOpen} 
        onClose={() => setViewOpen(false)} 
        title={<Text fw={700} size="lg">Expense Details</Text>} 
        size="lg" 
        centered
      >
         {selectedExpense && (
            <Stack gap="md">
                <Group justify="space-between">
                    <Text c="dimmed" size="sm">Date:</Text>
                    <Text fw={500}>{new Date(selectedExpense.date).toLocaleDateString()}</Text>
                </Group>
                <Divider />
                
                <Group justify="space-between">
                    <Text c="dimmed" size="sm">Title:</Text>
                    <Text fw={700} size="lg">{selectedExpense.title}</Text>
                </Group>
                
                <Group justify="space-between">
                    <Text c="dimmed" size="sm">Amount:</Text>
                    <Text fw={700} size="xl" c="red">₱{Number(selectedExpense.amount).toLocaleString()}</Text>
                </Group>

                <Group justify="space-between">
                    <Text c="dimmed" size="sm">Category:</Text>
                    <Badge color="gray">{selectedExpense.category}</Badge>
                </Group>

                <Group justify="space-between">
                    <Text c="dimmed" size="sm">Particulars:</Text>
                    <Text>{selectedExpense.particulars || 'N/A'}</Text>
                </Group>

                <Box>
                    <Text c="dimmed" size="sm" mb={5}>Remarks:</Text>
                    <Paper p="xs" withBorder bg="gray.0">
                        <Text size="sm">{selectedExpense.description || 'No remarks.'}</Text>
                    </Paper>
                </Box>

                <Box>
                    <Text c="dimmed" size="sm" mb={5}>Proof of Expense:</Text>
                    {selectedExpense.proofImage ? (
                        <Image 
                            src={selectedExpense.proofImage} 
                            radius="md" 
                            alt="Proof"
                            style={{ maxHeight: 400, objectFit: 'contain', border: '1px solid #eee' }} 
                        />
                    ) : (
                        <Card withBorder p="md" bg="gray.0">
                            <Group justify="center">
                                <IconMoodEmpty size={30} color="gray" />
                                <Text c="dimmed">No image attached.</Text>
                            </Group>
                        </Card>
                    )}
                </Box>

                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setViewOpen(false)}>Close</Button>
                </Group>
            </Stack>
         )}
      </Modal>
    </Layout>
  );
};

export default Expenses;